import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Undo2 } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const CHIP = {
  pending: ["Pending", "border-[#D9B47A]/50 text-[#D9B47A]"],
  approved: ["Shown publicly", "bg-[#C2414F] border-[#C2414F] text-[#F2E8DF]"],
  rejected: ["Not shown", "border-[#8F8176] text-[#A8998C]"],
};
const btn = "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors";

export default function ReflectionsAdmin({ http, withRefresh }) {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("pending");
  useEffect(() => {
    withRefresh(() => http.get("/admin/reflections"))
      .then(({ data }) => setList(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Could not load reflections."));
  }, [http, withRefresh]);

  const review = async (r, status) => {
    try {
      await withRefresh(() => http.patch(`/admin/reflections/${r.id}`, { status }));
      setList((l) => l.map((x) => (x.id === r.id ? { ...x, status } : x)));
      toast.success(status === "approved" ? "Reflection is now shown anonymously." : status === "rejected" ? "Reflection kept private." : "Moved back to pending.");
    } catch {
      toast.error("Could not update this reflection.");
    }
  };

  const rows = list ?? [];
  const shown = rows.filter((r) => filter === "all" || r.status === filter);
  return (
    <div data-testid="reflections-admin" className="mt-10">
      <div className="flex flex-wrap gap-2">
        {[["pending", "Pending"], ["approved", "Shown"], ["rejected", "Not shown"], ["all", "All"]].map(([k, l]) => (
          <button key={k} data-testid={`reflections-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full border px-4 py-2 text-sm transition-colors ${filter === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/30 text-[#CFC2B6] hover:border-[#D9B47A]"}`}>
            {l} · {k === "all" ? rows.length : rows.filter((r) => r.status === k).length}
          </button>
        ))}
      </div>
      {list !== null && shown.length === 0 && <p data-testid="reflections-empty" className="mt-12 font-display italic text-2xl text-[#A8998C]">No reflections here yet.</p>}
      <ul className="mt-6 border-t border-[#D9B47A]/15">
        {shown.map((r) => {
          const [label, cls] = CHIP[r.status] ?? CHIP.pending;
          return (
            <li key={r.id} data-testid={`reflection-row-${r.id}`} className="py-6 border-b border-[#D9B47A]/15 px-2 grid sm:grid-cols-12 gap-4">
              <div className="sm:col-span-8">
                <p className="font-display text-xl sm:text-2xl leading-snug">“{r.text}”</p>
                {r.gate_label && <p data-testid={`reflection-gate-${r.id}`} className="mt-2 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">After {r.gate_label}</p>}
                <p className="mt-3 text-sm text-[#A8998C]">{r.name || "—"} · {r.email} · {fmt(r.created_at)} <span className="text-[#8F8176]">(private — shown publicly as “An Initiate”)</span></p>
              </div>
              <div className="sm:col-span-4 flex sm:justify-end items-start gap-2 flex-wrap">
                <span className={`rounded-full border px-3 py-1 text-[12px] ${cls}`}>{label}</span>
                {r.alert && <span data-testid={`reflection-alert-${r.id}`} className="w-full sm:text-right font-tt-mono text-[11px] uppercase tracking-[0.15em] text-[#A8998C]">{{ sent: "Alert emailed to you", failed: "Alert email failed", pending: "Alert sending…" }[r.alert] ?? ""}</span>}
                {r.status !== "approved" && <button data-testid={`reflection-approve-${r.id}`} onClick={() => review(r, "approved")} className={`${btn} border-[#D9B47A]/60 hover:bg-[#D9B47A] hover:text-[#0A0706]`}><Check className="w-4 h-4" /> Approve</button>}
                {r.status !== "rejected" && <button data-testid={`reflection-reject-${r.id}`} onClick={() => review(r, "rejected")} className={`${btn} border-[#C2414F]/60 hover:bg-[#C2414F]`}><X className="w-4 h-4" /> {r.status === "approved" ? "Hide" : "Reject"}</button>}
                {r.status === "rejected" && <button data-testid={`reflection-pending-${r.id}`} onClick={() => review(r, "pending")} className={`${btn} border-[#8F8176] hover:border-[#D9B47A]`}><Undo2 className="w-4 h-4" /> Pending</button>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
